import React, { useState, useEffect, useRef } from "react";
import { X, UploadCloud, Trash2, Loader } from "lucide-react";
import { masterService } from "../../api/masterService";
import { useNotification } from "../../contexts/NotificationContext";
import SearchableSelect from "../SearchableSelect";
import ImageCropperModal from "../ImageCropperModal";

export default function AddStockLocationModal({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
}) {
  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    type: "",
    capacity: "",
    description: "",
    image_url: "",
    image_base64: "",
    is_active: true,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cropperModal, setCropperModal] = useState({
    isOpen: false,
    imageSrc: "",
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        code: editData.code || "",
        type: editData.type || "",
        capacity: editData.capacity || "",
        description: editData.description || "",
        image_url: editData.image_url || "",
        image_base64: "",
        is_active: editData.is_active !== false,
      });
      setImagePreview(editData.image_url || null);
    } else {
      setFormData({
        name: "",
        code: "",
        type: "",
        capacity: "",
        description: "",
        image_url: "",
        image_base64: "",
        is_active: true,
      });
      setImagePreview(null);
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError("Ukuran foto tidak boleh melebihi 5MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (!["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(file.type)) {
      showError("Format foto harus PNG, JPG, JPEG, atau WEBP");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCropperModal({
        isOpen: true,
        imageSrc: reader.result,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = (croppedBase64) => {
    const previewSrc = `data:image/jpeg;base64,${croppedBase64}`;
    setImagePreview(previewSrc);
    setFormData((prev) => ({
      ...prev,
      image_base64: croppedBase64,
      image_url: "",
    }));
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData((prev) => ({
      ...prev,
      image_base64: "",
      image_url: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showError("Nama lokasi wajib diisi");
      return;
    }

    if (!formData.type) {
      showError("Tipe lokasi wajib dipilih");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        type: formData.type,
        capacity: formData.capacity ? Number(formData.capacity) : 0,
        description: formData.description.trim(),
        image_url: formData.image_url,
        image_base64: formData.image_base64,
        is_active: formData.is_active,
      };

      if (editData) {
        await masterService.stockLocations.update(editData.id, payload);
        showSuccess(`Lokasi stok "${formData.name}" berhasil diperbarui`);
      } else {
        await masterService.stockLocations.create(payload);
        showSuccess(`Lokasi stok "${formData.name}" berhasil ditambahkan`);
      }
      onSuccess();
      onClose();
    } catch (err) {
      showError(err.message || "Gagal menyimpan lokasi stok");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-200">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-teal-600 text-white shrink-0 rounded-t-2xl">
            <h2 className="font-bold text-base tracking-tight">
              {editData ? "Edit Lokasi Stok" : "Tambah Lokasi Stok"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Modal Body Form */}
          <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
            <div className="p-6 space-y-5 text-sm text-slate-700 overflow-y-auto flex-1">
              {/* Nama Lokasi */}
              <div className="space-y-1.5">
                <label className="block font-semibold text-slate-800">
                  Nama Lokasi <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Masukkan nama lokasi"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-slate-800 placeholder:text-slate-400 text-sm transition"
                  required
                />
              </div>

              {/* Grid: Kode Lokasi & Tipe Lokasi */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block font-semibold text-slate-800">
                    Kode Lokasi <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: GDG-01"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-slate-800 placeholder:text-slate-400 text-sm transition"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block font-semibold text-slate-800">
                    Tipe <span className="text-rose-500">*</span>
                  </label>
                  <SearchableSelect
                    options={[
                      { id: "Gudang", name: "Gudang" },
                      { id: "Toko", name: "Toko" },
                      { id: "Rak", name: "Rak" },
                      { id: "Display", name: "Display" },
                      { id: "Transit", name: "Transit" },
                      { id: "Lainnya", name: "Lainnya" },
                    ]}
                    value={formData.type}
                    onChange={(val) => setFormData({ ...formData, type: val })}
                    placeholder="Pilih tipe lokasi"
                    displayKey="name"
                    valueKey="id"
                    searchable={false}
                  />
                </div>
              </div>

              {/* Kapasitas (unit) */}
              <div className="space-y-1.5">
                <label className="block font-semibold text-slate-800">
                  Kapasitas (unit) <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="number"
                  placeholder="Contoh: 5000"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-slate-800 placeholder:text-slate-400 text-sm transition"
                />
              </div>

              {/* Deskripsi (optional) */}
              <div className="space-y-1.5">
                <label className="block font-semibold text-slate-800">
                  Deskripsi <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <textarea
                    rows="3"
                    maxLength={200}
                    placeholder="Masukkan deskripsi lokasi"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-slate-800 placeholder:text-slate-400 text-sm transition resize-none pb-7"
                  />
                  <div className="absolute right-3 bottom-2 text-[11px] font-medium text-slate-400 pointer-events-none">
                    {formData.description.length}/200
                  </div>
                </div>
              </div>

              {/* Foto Lokasi (optional) */}
              <div className="space-y-1.5">
                <label className="block font-semibold text-slate-800">
                  Foto Lokasi <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  className="hidden"
                />

                {imagePreview ? (
                  <div className="relative rounded-2xl border border-slate-200 p-3 bg-slate-50 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {/* 4:3 Ratio Aspect Container */}
                      <div className="w-16 h-12 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-100 flex items-center justify-center">
                        <img
                          src={imagePreview}
                          alt="Foto Lokasi Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-xs text-slate-800 truncate">
                          Foto Lokasi Terpotong (4:3)
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          Klik hapus jika ingin mengunggah foto baru
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="p-2 rounded-xl text-rose-600 hover:bg-rose-50 transition-colors shrink-0"
                      title="Hapus Foto">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-200 hover:border-teal-500 rounded-2xl p-6 text-center bg-slate-50/50 hover:bg-teal-50/20 transition cursor-pointer group flex flex-col items-center justify-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-slate-100 group-hover:bg-teal-100 text-slate-500 group-hover:text-teal-600 flex items-center justify-center transition-colors">
                      <UploadCloud className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-xs group-hover:text-teal-700 transition-colors">
                        Klik untuk unggah foto lokasi
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        PNG, JPG, atau JPEG. Maks. 5MB (Format Ratio 4:3)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Status Switch Toggle */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Status Lokasi</p>
                  <p className="text-[11px] text-slate-400">
                    {formData.is_active
                      ? "Lokasi aktif dan dapat dipilih pada data produk"
                      : "Lokasi nonaktif (disembunyikan dari pilihan produk)"}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-white shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition text-sm">
                Batal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold flex items-center gap-2 rounded-xl shadow-sm transition text-sm">
                {loading && <Loader className="w-4 h-4 animate-spin" />}
                <span>Simpan</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 4:3 Image Cropper Modal */}
      <ImageCropperModal
        isOpen={cropperModal.isOpen}
        onClose={() => setCropperModal({ isOpen: false, imageSrc: "" })}
        imageSrc={cropperModal.imageSrc}
        onSave={handleCropSave}
        title="Potong Foto Lokasi (Rasio 4:3)"
      />
    </>
  );
}
